<template>
  <div class="timeslice" ref="timeslice" :class="{'is-timeslice-loading': isLoading}">
    <time-slice-loading v-if="isLoading" />
  </div>
</template>

<script>
  import * as THREE from 'three';

  import {createThreeBgScene} from "../../services/3d/timesliceBg";
  import {
    addPendingObjectToScene,
    getNumberOfArrowsInArray,
    initializeTimeslice,
    loadArrow
  } from "../../services/3d/timesliceUtils";
  import {createLine, fitCoordinatesIntoScene, initializeDrawing} from "../../services/3d/timesliceDraw";
  import {addArrow} from "../../services/3d/timesliceArrows";
  import TimeSliceLoading from "./TimeSliceLoading";
  import {
    addTimesliceRecord, createTimesliceRecording,
    printTimesliceRecording,
    setTimisliceInitData
  } from "../../services/modules/timeslice-recording";
  import {saveTimesliceRecording} from "../../services/apollo/timeslice";

  export default {
    name: 'TimeSlice',
    components: {TimeSliceLoading},
    data: () => ({
      renderActive: true,
      drawingsArray: null,
      drawScene: null,
      arrow: null,
      techArrow: null,
      scene: null,
      techObjects: [],
      userObjects: [],
      isLoading: true,
      isDrawing: false,
      isDrawingTech: false,
      currentLine: null,
      currentLineTech: null,
      drawSceneWidth: 0,
      drawSceneHeight: 0,
      stopDrawingTimeout: null,
      bufferObjects: [],
      currentBufferDraw: [],
      renderer: null
    }),

    props: {
      timesliceInicializationData: {
        type: Object
      },
      callSid: {
        type: String
      },
      shouldRecord: {
        type: Boolean,
        default: true
      }
    },

    methods: {
      undo(saveUndo = true) {
        let techObject = this.techObjects.pop();
        this.drawScene.remove(techObject);
        this.scene.remove(techObject);

        if(saveUndo) {
          addTimesliceRecord(this.recording, {
            type: 'undo',
            timestamp: Date.now(),
            actor: 'technician'
          });
        }
        this.animate()
      },
      clear() {
        while(this.techObjects.length) {
          this.undo(false)
        }

        addTimesliceRecord(this.recording, {
          type: 'clear',
          timestamp: Date.now(),
          actor: 'technician'
        });
      },
      undoUser(saveUndo = true) {
        if(!this.scene) {
          this.bufferObjects.pop()
          return
        }
        let userObject = this.userObjects.pop();
        this.drawScene.remove(userObject);
        this.scene.remove(userObject);

        if(saveUndo)  {
          addTimesliceRecord(this.recording, {
            type: 'undo',
            timestamp: Date.now(),
            actor: 'user'
          });
        }
        this.animate()
      },
      clearUser() {
        if(!this.scene) {
          while (this.bufferObjects.length) {
            this.undoUser(false)
          }
        } else {
          while(this.userObjects.length) {
            this.undoUser(false)
          }
        }

        addTimesliceRecord(this.recording, {
          type: 'clear',
          timestamp: Date.now(),
          actor: 'user'
        });
      },

      addTechArrow(arrowMatrix) {
        addTimesliceRecord(this.recording, {
          type: 'arrow',
          timestamp: Date.now(),
          arrowMatrix: arrowMatrix,
          actor: 'technician'
        });
        let numArrows = getNumberOfArrowsInArray(this.techObjects);
        let arrowMesh = addArrow(this.techArrow, arrowMatrix, numArrows + 1, this.timesliceInicializationData.transformCameraMatrix);
        if(this.scene) {
          this.scene.add(arrowMesh)
        }
        this.techObjects.push(arrowMesh);
        this.animate()
      },

      addUserArrow(arrowMatrix) {
        addTimesliceRecord(this.recording, {
          type: 'arrow',
          timestamp: Date.now(),
          arrowMatrix: arrowMatrix,
          actor: 'user'
        });
        if(!this.scene) {
          this.bufferObjects.push({
            type: 'arrow',
            matrix: arrowMatrix
          });
          return
        }
        let numArrows = getNumberOfArrowsInArray(this.userObjects);
        let arrowMesh = addArrow(this.arrow, arrowMatrix, numArrows + 1, this.timesliceInicializationData.transformCameraMatrix);
        if(this.scene) {
          console.log(this.scene)
          this.scene.add(arrowMesh)
        }
        this.userObjects.push(arrowMesh)
        this.animate()
      },

      mobileDraw(point) {
        if(!this.scene) {
          if(!this.isDrawing) {

            this.currentBufferDraw = {
              type: 'draw',
              points: [point.replace('C,', '').split(',')]
            };

            this.bufferObjects.push(this.currentBufferDraw);
            this.isDrawing = true;

          } else {
            this.currentBufferDraw && this.currentBufferDraw.points.push(point.replace('C,', '').split(','))
          }

          addTimesliceRecord(this.recording, {
            type: 'point',
            timestamp: Date.now(),
            point: point,
            actor: 'user'
          });

          return;
        }

        clearTimeout(this.stopDrawingTimeout);
        if(!this.isDrawing) {
          let {line, mesh} = createLine(0xEA2557);
          this.currentLine = line;
          this.userObjects.push(mesh);
          let [x, y, width, height] = point.replace('C,', '').split(',');
          let [sceneX, sceneY] = fitCoordinatesIntoScene(x, y, width, height, this.drawSceneWidth, this.drawSceneHeight);
          this.currentLine.addPoint({sceneX, sceneY});
          this.isDrawing = true;

          this.stopDrawingTimeout = setTimeout(() => {
            this.stopDrawing()
          }, 2000);

          if(this.drawScene) {
            this.drawScene.add(mesh)
          }
        } else {
          let [x, y, width, height] = point.replace('C,', '').split(',');
          let [sceneX, sceneY] = fitCoordinatesIntoScene(x, y, width, height, this.drawSceneWidth, this.drawSceneHeight);
          this.currentLine.addPoint({sceneX, sceneY});
        }

        addTimesliceRecord(this.recording, {
          type: 'point',
          timestamp: Date.now(),
          point: point,
          actor: 'user'
        });
        this.animate()
      },

      techDraw(point) {
        if(!this.isDrawingTech) {
          let {line, mesh} = createLine(0x08A5B4);
          this.currentLineTech = line;
          this.techObjects.push(mesh);
          let [x, y, width, height] = point.replace('C,', '').split(',');
          let [sceneX, sceneY] = fitCoordinatesIntoScene(x, y, width, height, this.drawSceneWidth, this.drawSceneHeight);
          this.currentLineTech.addPoint({sceneX, sceneY});
          this.isDrawingTech = true;

          if(this.drawScene) {
            this.drawScene.add(mesh)
          }
        } else {
          let [x, y, width, height] = point.replace('C,', '').split(',');
          let [sceneX, sceneY] = fitCoordinatesIntoScene(x, y, width, height, this.drawSceneWidth, this.drawSceneHeight);
          this.currentLineTech.addPoint({sceneX, sceneY});
        }
        this.animate()
      },

      stopDrawing() {
        this.isDrawing = false;
        if(!this.scene && this.currentBufferDraw) {
          this.currentBufferDraw.finished = true
        }
        clearTimeout(this.stopDrawingTimeout);
        addTimesliceRecord(this.recording, {
          type: 'stopDrawing',
          timestamp: Date.now(),
          actor: 'user'
        });
        this.animate()
      },

      stopDrawingTech() {
        this.isDrawingTech = false;
        this.animate()
      },

      takeScreenShot() {
        if(!this.isLoading) {
          this.renderActive = false;

          this.animate(() => {
            this.$nextTick()
              .then(() => {
                let dataURI = this.renderer.domElement.toDataURL('image/jpeg');
                this.$store.dispatch('screenshots/uploadScreenshot', {image: dataURI, callSid: this.callSid})
              })
          })
        }
      },

      animate(cb) {
        requestAnimationFrame(() => {
          this.renderer.clear();
          this.renderer.render(this.backgroundScene, this.backgroundCamera);
          this.renderer.render(this.scene, this.camera);
          this.renderer.render(this.drawScene, this.drawCamera);
          cb && cb()
        })
      },

      initialize() {
        if(!this.$refs.timeslice) {
          return
        }

        setTimisliceInitData(this.recording, this.timesliceInicializationData);

        Promise.all([
          initializeTimeslice(this.timesliceInicializationData, this.$refs.timeslice),
          createThreeBgScene(this.timesliceInicializationData.backgroundImage, this.timesliceInicializationData.width/this.timesliceInicializationData.height),
          loadArrow()
        ])
          .then(([
                   {renderer, camera, scene, debugCamera, debugControls},
                   {backgroundScene, backgroundCamera},
                   arrow
                 ]) => {

            this.isLoading = false;
            renderer.domElement.id = 'timeslice'
            this.renderer = renderer

            let {drawCamera, drawScene, drawSceneWidth, drawSceneHeight} = initializeDrawing(renderer, this.techObjects, (isDraw, coordinates, stop) => {
              if(stop) {
                this.$emit('stop');

                addTimesliceRecord(this.recording, {
                  type: 'stopDrawing',
                  timestamp: Date.now(),
                  actor: 'technician'
                });

                return
              }

              if(!isDraw) {
                this.$emit('click', coordinates)
              } else {
                this.$emit('draw', coordinates)
                let {originalX, originalY, videoWidth, videoHeight} = coordinates;
                addTimesliceRecord(this.recording, {
                  type: 'point',
                  timestamp: Date.now(),
                  point: `C,${originalX},${originalY},${videoWidth},${videoHeight}`,
                  actor: 'technician'
                });
              }
              this.animate()
            });

            this.drawScene = drawScene;
            this.drawCamera = drawCamera;
            this.drawSceneWidth = drawSceneWidth;
            this.drawSceneHeight = drawSceneHeight;

            this.backgroundScene = backgroundScene;
            this.backgroundCamera = backgroundCamera;
            this.scene = scene;
            this.camera = camera;
            this.arrow = arrow;

            this.techArrow = arrow.clone();
            this.techArrow.children[0].material = new THREE.MeshStandardMaterial({color: 0x08A5B4});

            /*
              Initialize objects  that were already drawn on mobile side before timeslice was initialized
             */
            this.bufferObjects.forEach((obj) => {
              if(obj.type === 'arrow') {
                this.addUserArrow(obj.matrix)
              } else if(obj.type === 'draw')  {
                let points = obj.points.map(([x, y, width, height]) =>  {
                  return fitCoordinatesIntoScene(x, y, width, height, this.drawSceneWidth, this.drawSceneHeight)
                });

                let {line, mesh} = createLine(0xEA2557, points);
                this.userObjects.push(mesh);
                this.drawScene.add(mesh);

                if(!obj.finished) {
                  this.currentLine = line
                }
              }
            });

            console.log(this.bufferObjects, this.userObjects);

            this.animate();
            // let animate = () => {
            //   if(this.renderActive) {
            //     requestAnimationFrame(animate);
            //   }
            //   debugControls.update();
            //   renderer.clear();
            //   renderer.render(backgroundScene, backgroundCamera);
            //   renderer.render(scene, camera);
            //   renderer.render(drawScene, drawCamera);
            // };
            //
            // animate()

          });
      }
    },

    mounted() {
      this.recording = createTimesliceRecording();
      console.log("INIT")
      if(this.timesliceInicializationData) {
        console.log("IN INIT")
        this.initialize()
      }
    },

    beforeDestroy() {
      try {
        this.renderer.dispose()
      } catch (e) {}

      this.renderActive = false;
      this.recording.initData.endedAt = Date.now();
      if(this.shouldRecord) {
        saveTimesliceRecording(this.callSid, this.recording)
          .catch()
      }
    }
  }
</script>

<style lang="scss">
@import "Timeslice";
</style>
